import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
// eslint-disable-next-line
import L from 'leaflet';

const mapContainer = 'tzo-itinerary-map';
const container = document.getElementById(mapContainer);
const scrollbox = document.getElementById('js-itinerary-map-scrollbox');
const sidebar = document.getElementById('js-itinerary-sidebar');
let isHorizontalScroll = window.innerWidth < 1024;

mapboxgl.accessToken = container.getAttribute('data-map-box-token');

let map = {};
let days = [];
let adjustMapOnScroll = true;
// eslint-disable-next-line
let selectedDay = 0; // Variable to store the selected day

/*
    show all the children for a given index (i.e. index "1" = show all children for day 1)
*/
function showIndex(index) {

    const bounds = new mapboxgl.LngLatBounds();

    const parents = document.getElementsByClassName('itinerary-map-parent');

    for (let i = 0; i < parents.length; i++) {

        const parent = parents[i];
        const day = parseInt(parent.dataset.index, 10);

        parent.classList.remove('active');
        parent.classList.remove('inactive');

        // if index === 0 then we are resetting the map! reset bounds to parent coords
        if (index === 0) {

            bounds.extend(JSON.parse(parent.dataset.coords));

        }

        if (day === index) {

            parent.classList.add('active');

        } else {

            parent.classList.add('inactive');

        }

    }

    const children = document.getElementsByClassName('itinerary-map-child');

    for (let i = 0; i < children.length; i++) {

        const child = children[i];
        const day = parseInt(child.dataset.day, 10);

        child.classList.remove('flex');
        child.classList.add('hidden');

        if (index === day) {

            child.classList.remove('hidden');
            child.classList.add('flex');

            bounds.extend(JSON.parse(child.dataset.coords));

        }

    }

    if (!map.fitBounds) {

        return;

    }

    /* eslint-disable-next-line */
    const padding = isHorizontalScroll ? { top: 75, bottom: 250, left: 75, right: 75 } : 75;
    map.fitBounds(bounds, {
        /* eslint-disable-next-line */
        padding: padding,
        animate: true,
        duration: 1000,
    });

}

/*
    reset the map to its starting point
*/
function resetMap() {

    adjustMapOnScroll = false;

    selectedDay = 0; // Reset selected day to 0

    /* eslint-disable-next-line */
    showIndex(0);

    // remove inactive class from all parent points
    const parents = document.getElementsByClassName('itinerary-map-parent');

    for (let i = 0; i < parents.length; i++) {

        const parent = parents[i];
        parent.classList.remove('inactive');

    }

    scrollbox.scrollTop = 0;

}

/*
    Using mapbox's 'LngLatBounds' method,
    create a square around all the listings
    for a given day.
    Then, find the centre of that square:
    that's where the day's centre point
    needs to be!
*/
function findCentres() {

    const jsonDays = JSON.parse(container.getAttribute('data-days'));

    for (let i = 0; i < jsonDays.length; i++) {

        const listings = jsonDays[i].listings;

        const bounds = new mapboxgl.LngLatBounds();

        for (let j = 0; j < listings.length; j++) {

            bounds.extend([listings[j].longitude, listings[j].latitude]);

            if (j === listings.length - 1) {

                const centre = bounds.getCenter();

                jsonDays[i].centre.latitude = centre.lat;
                jsonDays[i].centre.longitude = centre.lng;

                if (i === jsonDays.length - 1) {

                    return jsonDays;

                }

            }

        }

    }

    return jsonDays;

}

/*
    Given a set of lat and lng attributes,
    create a marker element to add to the map
*/
function createMarkerEl(lat, lng) {

    const el = document.createElement('div');

    el.className = 'itinerary-map-marker';

    el.dataset.coords = '[' + lng + ',' + lat + ']';

    return el;

}

/*
    Add a child marker (and create a popup when the marker is clicked)
*/
function addChildMarker({
    title = '',
    lat = '',
    lng = '',
    iconSVG = '',
    index = 0,
}) {

    const el = createMarkerEl(lat, lng);

    el.dataset.day = index;
    el.classList.add('itinerary-map-child');
    el.classList.add('hidden');
    el.title = title;
    el.innerHTML = iconSVG;

    const popup = new mapboxgl.Popup({})
        .setHTML('<p class="h6 m-0 pr-[1.25rem]">' + title + '</p>');

    new mapboxgl.Marker(el)
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map);

}

/*
    Create a parent marker (and open its children when it is clicked)
*/
function addParentMarker({
    title = '',
    lat = '',
    lng = '',
    index = 0,
    hasOneListing = false,
    z = 1,
}) {

    const el = createMarkerEl(lat, lng);

    el.classList.add('itinerary-map-parent');
    if (hasOneListing) {

        el.classList.add('one-child');

    }
    el.dataset.index = index;
    el.innerHTML = index;
    el.style.zIndex = z;
    el.addEventListener('click', function hasClicked(event) {

        if (event.target.classList.contains('active')) {

            resetMap();

            return;

        }

        showIndex(index);
        selectedDay = index; // Save the selected day

        /* eslint-disable-next-line */
        if (!Alpine.store('sidebar').open) {

            /* eslint-disable-next-line */
            Alpine.store('sidebar').toggle();
            document.getElementById('js-itinerary-sidebar').classList.toggle('lg:!-translate-x-full');

            /* eslint-disable-next-line */
            setTimeout(function () {

                /* eslint-disable-next-line */
                scrollToDay(index);

            }, 300);

        } else {

            /* eslint-disable-next-line */
            scrollToDay(index);

        }

    });
    el.title = 'Day ' + index + ': ' + title;

    new mapboxgl.Marker(el).setLngLat([lng, lat]).addTo(map);

}

/*
    iterate through all of the days, and generate the necessary markers
*/
function addMarkers() {

    const bounds = new mapboxgl.LngLatBounds();

    for (let i = 0; i < days.length; i++) {

        const day = days[i];
        // Lower days should get higher priority for zIndex in case they overlap
        const zIndex = days.length - i;

        addParentMarker({
            title: day.title,
            lat: parseFloat(day.centre.latitude),
            lng: parseFloat(day.centre.longitude),
            index: i + 1,
            hasOneListing: day.listings.length === 1,
            z: zIndex,
        });

        bounds.extend([parseFloat(day.centre.longitude), parseFloat(day.centre.latitude)]);

        if (i === days.length - 1) {

            if (!map.fitBounds) {

                return;

            }

            /* eslint-disable-next-line */
            const padding = isHorizontalScroll ? { top: 75, bottom: 250, left: 75, right: 75 } : 150;
            map.fitBounds(bounds, {
                /* eslint-disable-next-line */
                padding: padding,
            });

        }

        if (day.listings) {

            for (let j = 0; j < day.listings.length; j++) {

                const listing = day.listings[j];

                addChildMarker({
                    title: listing.title,
                    lat: parseFloat(listing.latitude),
                    lng: parseFloat(listing.longitude),
                    iconSVG: listing.iconSVG,
                    index: i + 1,
                });

            }

        }

    }

}

/*
    check whether a given day is visible in the scrollable element
*/
function isDayOnScreen(i) {

    const scrollboxBounds = scrollbox.getBoundingClientRect();
    const centerScrollboxBounds = (scrollboxBounds.top + scrollboxBounds.bottom) / 2;

    const element = document.getElementById('js-itinerary-day-' + i);
    const bounds = element.getBoundingClientRect();

    if (isHorizontalScroll) {

        return (
            /* eslint-disable-next-line */
            bounds.top >= 0 &&
            /* eslint-disable-next-line */
            bounds.left >= 0 &&
            /* eslint-disable-next-line */
            bounds.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            /* eslint-disable-next-line */
            bounds.right <= (window.innerWidth || document.documentElement.clientWidth)
        );

    }

    return bounds.top < centerScrollboxBounds && bounds.bottom > centerScrollboxBounds;

}

/*
    check whether the main itinerary detail visible in the scrollable element on mobile
*/
function isDetailsOnScreen() {

    const element = document.getElementById('js-itinerary-details');
    const bounds = element.getBoundingClientRect();

    return (
        /* eslint-disable-next-line */
        isHorizontalScroll &&
        /* eslint-disable-next-line */
        bounds.top >= 0 &&
        /* eslint-disable-next-line */
        bounds.left >= 0 &&
        /* eslint-disable-next-line */
        bounds.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        /* eslint-disable-next-line */
        bounds.right <= (window.innerWidth || document.documentElement.clientWidth)
    );

}

/*
    Scroll to a specific day!
*/
function scrollToDay(i) {

    adjustMapOnScroll = false;

    scrollbox.scrollTop = 0;

    const scrollboxBounds = scrollbox.getBoundingClientRect();

    const element = document.getElementById('js-itinerary-day-' + i);

    const bounds = element.getBoundingClientRect();

    scrollbox.scrollTop = bounds.top - scrollboxBounds.top;

}

/*
    find the first day which is currently visible in the scrollable element
*/
function handleScroll() {

    const popups = document.getElementsByClassName('mapboxgl-popup');

    if (popups.length) {

        for (let i = 0; i < popups.length; i++) {

            popups[i].remove();

        }

    }

    if (!adjustMapOnScroll) {

        adjustMapOnScroll = true;

        return;

    }

    if (isHorizontalScroll) {

        if (isDetailsOnScreen()) {

            resetMap();

        }

    } else if (scrollbox.scrollTop === 0) {

        resetMap();

        return;

    }

    for (let i = 0; i < days.length; i++) {

        if (isDayOnScreen(i + 1)) {

            showIndex(i + 1);
            selectedDay = i + 1; // Update the selected day when scrolling
            break;

        }

    }

}

/*
    initialise the map!
*/
function mapInit() {

    days = findCentres();

    setTimeout(function doMapInit() {

        const isTouchscreen = window.matchMedia('(pointer: coarse)').matches;

        map = new mapboxgl.Map({
            container: mapContainer, // container ID
            style: 'mapbox://styles/mapbox/streets-v12', // style URL
            center: [-89.000000, 40.000000], // starting position [lng, lat]
            zoom: 9, // starting zoom
            maxZoom: 17,
            scrollZoom: false, // prevent map from zooming when user scrolls
            // prevent map from panning when user scrolls on touchscreens
            cooperativeGestures: isTouchscreen,
        });

        map.addControl(new mapboxgl.NavigationControl());

        addMarkers();

    }, 0);

    scrollbox.addEventListener('scroll', handleScroll);

    // Ensure map stays centered and sidebar scrolls to active day when sidebar is toggled
    sidebar.addEventListener('transitionend', (event) => {

        // Opacity event firing on sidebar scroll which was causing bugginess
        if (event.propertyName !== 'opacity') {

            const activeParent = document.querySelector('.itinerary-map-parent.active');
            if (activeParent) {

                const index = parseInt(activeParent.dataset.index, 10);
                showIndex(index);
                scrollToDay(index);

            } else if (selectedDay) {

                showIndex(selectedDay);
                scrollToDay(selectedDay);

            } else {

                resetMap();

            }

        }

    });

    window.addEventListener('resize', () => {

        if (!map.fitBounds) {

            return;

        }

        isHorizontalScroll = window.innerWidth < 1024;

        if (selectedDay) {

            showIndex(selectedDay);

        } else {

            resetMap();

        }

    });

}

mapInit();
